import { useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TFSLoader } from '../components/ui';
import { Pagination } from '../components/common';
import { POItemDetailModal } from '../components/features';
import { poItemsAPI } from '../api';

const POItemsSearch = () => {
  const [searchParams, setSearchParams] = useState({
    po_number: '',
    item_code: '',
    quantity: ''
  });
  const [results, setResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 4;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (page = 1) => {
    if (!searchParams.po_number && !searchParams.item_code && !searchParams.quantity) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await poItemsAPI.searchPOItems({
        ...searchParams,
        page,
        page_size: pageSize
      });
      
      // Handle the pagination structure from API
      setResults(data.items || []);
      setTotalPages(data.pagination?.total_pages || 0);
      setTotalItems(data.pagination?.total_items || 0);
      setCurrentPage(data.pagination?.page || page);
      setHasSearched(true);
    } catch (error) {
      console.error('Failed to search PO items:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to search PO items. Please try again.';
      setError(errorMessage);
      setResults([]);
      setTotalPages(0);
      setTotalItems(0);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchParams({
      po_number: '',
      item_code: '',
      quantity: ''
    });
    setResults([]);
    setError(null);
    setHasSearched(false);
    setCurrentPage(1);
    setTotalPages(0);
    setTotalItems(0);
  };

  const handlePageChange = (page) => {
    handleSearch(page);
  };

  const handleRowClick = (item) => {
    setSelectedItem(item);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900">PO Items Search</h1>
        <p className="text-gray-600">Search for purchase order items by PO number, item code, or quantity</p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label htmlFor="po_number" className="block text-sm font-medium text-gray-700 mb-2">
              PO Number
            </label>
            <input
              type="text"
              id="po_number"
              name="po_number"
              value={searchParams.po_number}
              onChange={handleInputChange}
              placeholder="e.g., PO12345"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="item_code" className="block text-sm font-medium text-gray-700 mb-2">
              Item Code
            </label>
            <input
              type="text"
              id="item_code"
              name="item_code"
              value={searchParams.item_code}
              onChange={handleInputChange}
              placeholder="e.g., ITEM001"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
              Quantity
            </label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={searchParams.quantity}
              onChange={handleInputChange}
              placeholder="e.g., 100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => handleSearch(1)}
            disabled={loading || (!searchParams.po_number && !searchParams.item_code && !searchParams.quantity)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            Search
          </button>
          <button
            onClick={handleClear}
            className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            <XMarkIcon className="h-5 w-5 mr-2" />
            Clear
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <TFSLoader />
      ) : hasSearched && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Search Results {totalItems > 0 && `(${totalItems} items)`}
            </h2>
          </div>

          {results.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PO Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        SKU
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit Price
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {results.map((item, index) => (
                      <tr
                        key={item.id || index}
                        onClick={() => handleRowClick(item)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item['PO Number'] || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {item['Item Code'] || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {item.SKU || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {item['Item Quantity'] || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">
                          {item.Vendor || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {item['Item Unit Price'] ? `$${item['Item Unit Price'].toLocaleString()}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={handlePageChange}
                    nextPage={() => handlePageChange(currentPage + 1)}
                    prevPage={() => handlePageChange(currentPage - 1)}
                    hasNextPage={currentPage < totalPages}
                    hasPrevPage={currentPage > 1}
                    startIndex={(currentPage - 1) * pageSize}
                    endIndex={currentPage * pageSize}
                    totalItems={totalItems}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="p-12 text-center">
              <p className="text-gray-500">No results found. Try adjusting your search criteria.</p>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selectedItem && (
        <POItemDetailModal
          item={selectedItem}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default POItemsSearch;
